"use client"

import React, { useState, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { Info, Calendar as CalendarIcon, X, List } from "lucide-react"
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { Calendar } from "@/components/ui/calendar"
import { DateTime } from "luxon"
import { ru } from "date-fns/locale"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { cn, formatCurrency } from "@/lib/utils"
import type { AppointmentCreateErrors } from "@/lib/validation/appointmentCreate"
import { getErrorMessage } from "@/lib/i18n/errors"
import { useAppointmentCreateModal } from "@/contexts/AppointmentCreateModalContext"
import { useAvailableServices } from "@/hooks/modal/useAvailableServices"
import { useClientPhoneAutocomplete } from "@/hooks/modal-create/useClientPhoneAutocomplete"
import { salonPhoneCountry } from "@/lib/phone/salonCountry"
import { useStaffScheduleByDate } from "@/hooks/calendar/useStaffScheduleByDate"
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select"
import { useSpecialistsForDate } from "@/hooks/calendar/useSpecialistsForDate"
import { useCompanySettingsInfo } from "@/hooks/shared/useCompanySettingsInfo"
import { BusyOverlapWarning } from "@/components/custom/appointment-modal/busy-overlap-warning"
// Centralized cost calculation engine - single source of truth
import { createServiceFromCatalog, applyQuantityChange } from "@/lib/appointment/costEngine"





function formatDate(dateStr?: string | null) {
  if (!dateStr) return "—";
  const d = DateTime.fromISO(dateStr);
  if (!d.isValid) return "—";
  // Format as DD.MM HH:mm
  return `${d.day.toString().padStart(2, "0")}.${d.month.toString().padStart(2, "0")} ${d.hour.toString().padStart(2, "0")}:${d.minute.toString().padStart(2, "0")}`;
}

function useDebouncedHandler<T>(handler: (value: T) => void, delay = 500) {
  const timeout = useRef<NodeJS.Timeout | null>(null)
  const handlerRef = useRef(handler)
  handlerRef.current = handler

  return (value: T) => {
    if (timeout.current) clearTimeout(timeout.current)
    timeout.current = setTimeout(() => {
      handlerRef.current(value)
    }, delay)
  }
}

// Format seconds to "X h Y min"
function formatDuration(seconds?: number, t?: (key: string, defaultValue: string) => string) {
  if (!seconds || isNaN(seconds)) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  let result = "";
  if (h > 0) result += `${h} ${t ? t("common.time.hour_short", "h") : "h"} `;
  if (m > 0) result += `${m} ${t ? t("common.time.min_short", "min") : "min"}`;
  return result.trim() || `0 ${t ? t("common.time.min_short", "min") : "min"}`;
}

export function DetailsSectionCreate({
  errors,
  onClearError,
}: {
  errors?: AppointmentCreateErrors;
  onClearError?: (field: keyof AppointmentCreateErrors) => void;
}) {
  const { t } = useTranslation();
  const { appointment, updateAppointment } = useAppointmentCreateModal();
  const { data: companySettings } = useCompanySettingsInfo();
  const defaultPhoneCountry = salonPhoneCountry(companySettings?.country_iso2, companySettings?.phone_code);

  // Local state for debounced fields
  const [name, setName] = useState(appointment?.client?.name || "");
  const [phone, setPhone] = useState(appointment?.client?.phone || "");
  const [email, setEmail] = useState(appointment?.client?.email || "");
  const [comment, setComment] = useState(appointment?.comment || "");

  // Phone autocomplete dropdown control
  const [showPhoneSuggestions, setShowPhoneSuggestions] = useState(false);

  // Always call the autocomplete hook (Rules of Hooks)
  // Extract just the national number digits (strip +XXX country code) for autocomplete
  const phoneDigitsForAutocomplete = phone ? phone.replace(/^\+\d{1,3}/, '').replace(/\D/g, '') : '';
  const { data: suggestions, isLoading: isPhoneLoading } = useClientPhoneAutocomplete(
    showPhoneSuggestions && phoneDigitsForAutocomplete.length >= 3 ? phoneDigitsForAutocomplete : ""
  );

  // Debounced handler for name
  const handleNameChange = useDebouncedHandler((v: string) => {
    if (!appointment?.client) return;
    updateAppointment({
      client: { ...appointment.client, name: v }
    });
  }, 1000);

  // Debounced handler for phone - now receives full E.164 format
  const handlePhoneChange = useDebouncedHandler((v: string) => {
    if (!appointment?.client) return;
    updateAppointment({
      client: { ...appointment.client, phone: v }
    });
  }, 500);

  // Debounced handler for email
  const handleEmailChange = useDebouncedHandler((v: string) => {
    if (!appointment?.client) return;
    updateAppointment({
      client: { ...appointment.client, email: v }
    });
  }, 500);

  // Debounced handler for comment
  const handleCommentChange = useDebouncedHandler((v: string) => {
    updateAppointment({ comment: v });
  }, 500);

  // Sync local state if context changes (e.g., modal opens with new appointment)
  useEffect(() => {
    setName(appointment?.client?.name || "");
    setPhone(appointment?.client?.phone || "");
    setEmail(appointment?.client?.email || "");
    setComment(appointment?.comment || "");
  }, [appointment?.client?.name, appointment?.client?.phone, appointment?.client?.email, appointment?.comment]);

  // --- Duration state and helpers (lifted to top level) ---
  // Helper: compute duration from startTime and endTime
  function computeDuration(startTime?: string, endTime?: string) {
    if (!startTime || !endTime) return { hours: 0, minutes: 0 };
    const [sh, sm] = startTime.split(":").map(Number);
    const [eh, em] = endTime.split(":").map(Number);
    let start = sh * 60 + sm;
    let end = eh * 60 + em;
    if (end < start) end += 24 * 60; // handle overnight
    const totalMinutes = end - start;
    return {
      hours: Math.floor(totalMinutes / 60),
      minutes: totalMinutes % 60,
    };
  }

  // Helper: add duration to startTime, return "HH:mm"
  function addDurationToStart(startTime: string, hours: number, minutes: number) {
    const [sh, sm] = startTime.split(":").map(Number);
    let totalMinutes = sh * 60 + sm + hours * 60 + minutes;
    totalMinutes = ((totalMinutes % (24 * 60)) + (24 * 60)) % (24 * 60); // wrap around 24h
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  }

  // Duration state is always in sync with appointment times
  const [duration, setDuration] = React.useState(() =>
    computeDuration(appointment?.startTime, appointment?.endTime)
  );
  // Sync duration state with appointment times
  React.useEffect(() => {
    setDuration(computeDuration(appointment?.startTime, appointment?.endTime));
  }, [appointment?.startTime, appointment?.endTime]);

  if (!appointment) {
    return (
      <div className="flex-1 overflow-y-auto p-6 flex items-center justify-center text-gray-500">
        {t("appointment.create.no_data", "No appointment data.")}
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div>
        <h2 className="text-base font-medium mb-4">{t("appointment.create.client", "Client")}</h2>

        <div className="mb-4">
          <label className="block text-xs mb-1">{t("appointment.create.name", "Name")}</label>
          <Input
            value={name}
            aria-invalid={!!errors?.name}
            placeholder={t("appointment.create.name_placeholder", "Enter client name")}
            className={cn(
              "border-gray-300 rounded h-9 text-sm",
              errors?.name && "border-red-500 focus-visible:ring-red-500"
            )}
            onChange={(e) => {
              setName(e.target.value);
              onClearError?.("name");
              handleNameChange(e.target.value);
            }}
          />
          {errors?.name && (
            <p className="mt-1 text-xs text-red-600">{getErrorMessage(errors.name)}</p>
          )}
        </div>

        <div className="mb-4">
          <div className="flex items-center mb-1">
            <label className="block text-xs">{t("appointment.create.phone", "Phone")}</label>
            <Info className="w-3.5 h-3.5 ml-1 text-gray-400" />
          </div>
          <div className="relative">
            <PhoneInput
              international
              defaultCountry={defaultPhoneCountry}
              countryCallingCodeEditable={false}
              countryOptionsOrder={[
                'BY', 'RU', 'UA', 'PL', 'LT', 'LV', 'DE', 'US', 'GB', 'IL',
                '|',
                '...',
              ]}
              value={phone}
              onFocus={() => setShowPhoneSuggestions(true)}
              onBlur={() => setTimeout(() => setShowPhoneSuggestions(false), 200)}
              onChange={(value) => {
                const newValue = value || '';
                setPhone(newValue);
                onClearError?.("phone");
                handlePhoneChange(newValue);
              }}
              className={cn(
                "phone-input-no-zoom flex h-9 w-full rounded-md border border-input bg-background text-sm ring-offset-background",
                "[&_.PhoneInputInput]:flex-1 [&_.PhoneInputInput]:bg-transparent [&_.PhoneInputInput]:outline-none [&_.PhoneInputInput]:px-2",
                "[&_.PhoneInputCountry]:pl-3 [&_.PhoneInputCountry]:pr-1",
                errors?.phone && "border-red-500 focus-within:ring-red-500"
              )}
            />
            {errors?.phone && (
              <p className="mt-1 text-xs text-red-600">
                {getErrorMessage(errors?.phone)}
              </p>
            )}
            {/* Phone autocomplete popup */}
            {showPhoneSuggestions && phoneDigitsForAutocomplete.length >= 3 && (() => {
              // Handler for selecting a suggestion
              // Service returns: { id, displayName, phone, phoneCode, email }
              const handleSelectSuggestion = (suggestion: any) => {
                // Construct full E.164 phone: +{phoneCode}{digits}
                const phoneDigits = suggestion.phone.replace(/\D/g, '');
                const fullPhone = `+${suggestion.phoneCode || '375'}${phoneDigits}`;

                setPhone(fullPhone);
                setName(suggestion.displayName);
                setEmail(suggestion.email || "");
                handlePhoneChange(fullPhone);
                handleNameChange(suggestion.displayName);
                handleEmailChange(suggestion.email || "");
                updateAppointment({
                  client: {
                    ...appointment.client,
                    id: suggestion.id,
                    name: suggestion.displayName,
                    phone: fullPhone,
                    email: suggestion.email || "",
                  }
                });
                setShowPhoneSuggestions(false);
              };

              if (isPhoneLoading) {
                return (
                  <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded shadow z-10 text-xs p-2">
                    {t("common.loading", "Loading...")}
                  </div>
                );
              }
              if (Array.isArray(suggestions) && suggestions.length > 0) {
                return (
                  <ul className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded shadow z-10 text-xs max-h-48 overflow-y-auto">
                    {suggestions.map((s: any) => (
                      <li
                        key={s.id}
                        className="cursor-pointer px-3 py-2 hover:bg-amber-100"
                        onClick={() => handleSelectSuggestion(s)}
                      >
                        <div className="font-medium">{s.displayName}</div>
                        <div className="text-gray-500">+{s.phoneCode} {s.phone}</div>
                      </li>
                    ))}
                  </ul>
                );
              }
              return null;
            })()}
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-xs mb-1">Email</label>
          <Input
            value={email}
            placeholder={t("appointment.create.email_placeholder", "Enter client email")}
            className="border-gray-300 rounded h-9 text-sm"
            onChange={e => {
              setEmail(e.target.value);
              handleEmailChange(e.target.value);
            }}
          />
        </div>

        {/* Master Selection */}
        <div className="mb-4">
          <h2 className="text-base font-medium mb-1">{t("appointment.create.specialist", "Specialist")}</h2>
          {(() => {
            // Get current date from appointment
            const appointmentDate = appointment?.date;

            // Fetch specialists available for the appointment date
            const { data: specialists, isLoading: isLoadingMasters, error: mastersError } =
              useSpecialistsForDate(appointmentDate || "");

            // Handler for master change
            const handleMasterChange = (newMasterId: string) => {
              const masterId = Number(newMasterId);
              const selectedMaster = specialists?.find(s => s.id === masterId);
              if (selectedMaster) {
                updateAppointment({
                  specialistId: masterId,
                  staff: {
                    id: masterId,
                    name: selectedMaster.name,
                    specialization: selectedMaster.role,
                    avatar: selectedMaster.avatar,
                  },
                  // Clear services when master changes as they may differ
                  services: [],
                });
              }
            };

            // Current master ID
            const currentMasterId = appointment?.staff?.id ? String(appointment.staff.id) : "";

            if (isLoadingMasters) {
              return <div className="text-xs text-gray-500 py-2">{t("appointment.create.loading_specialists", "Loading specialists...")}</div>;
            }

            if (mastersError) {
              return <div className="text-xs text-red-500 py-2">{t("appointment.create.specialists_error", "Error loading specialists")}</div>;
            }

            if (!specialists || specialists.length === 0) {
              return <div className="text-xs text-gray-500 py-2">{t("appointment.create.no_specialists", "No specialists available for this date")}</div>;
            }

            return (
              <Select value={currentMasterId} onValueChange={handleMasterChange}>
                <SelectTrigger className="w-full h-9 text-sm">
                  <SelectValue placeholder={t("appointment.create.select_specialist", "Select specialist")} />
                </SelectTrigger>
                <SelectContent>
                  {specialists.map((master) => (
                    <SelectItem key={master.id} value={String(master.id)}>
                      <div className="flex items-center">
                        <span>{master.name}</span>
                        {master.role && (
                          <span className="ml-2 text-gray-500 text-xs">({master.role})</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            );
          })()}
        </div>

        <h2 className="text-base font-medium mb-1">{t("appointment.create.services", "Services")}</h2>
        {errors?.services && (
          <p className="mb-2 text-xs text-red-600">{getErrorMessage(errors.services)}</p>
        )}
        {(() => {
          // Service picker logic
          const masterId = appointment?.staff?.id ? Number(appointment.staff.id) : undefined;
          const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
          const [serviceSearch, setServiceSearch] = useState("");
          const { data: availableServices, isLoading: isLoadingServices, error: servicesError } =
            useAvailableServices(masterId);

          // Utility to add seconds to a "HH:mm" time string
          function addSecondsToTime(time: string, seconds: number) {
            const [h, m] = time.split(":").map(Number);
            const totalMinutes = h * 60 + m + Math.floor(seconds / 60);
            const newH = Math.floor(totalMinutes / 60) % 24;
            const newM = totalMinutes % 60;
            return `${String(newH).padStart(2, "0")}:${String(newM).padStart(2, "0")}`;
          }

          const handleAddService = (serviceId: number) => {
            if (!availableServices || !appointment) return;
            const selected = availableServices.find(s => s.id === serviceId);
            if (!selected) return;

            // Use centralized service creation from costEngine
            const newService = createServiceFromCatalog(selected);

            let newServices = [];
            if (Array.isArray(appointment.services) && appointment.services.some(s => s.id === serviceId)) {
              // Increment quantity using centralized applyQuantityChange
              newServices = appointment.services.map(s => {
                if (s.id === serviceId) {
                  const patch = applyQuantityChange(s as any, (s.amount || 1) + 1);
                  return { ...s, ...patch };
                }
                return s;
              });
            } else {
              newServices = Array.isArray(appointment.services)
                ? [...appointment.services, newService]
                : [newService];
            }
            // Sum total duration in seconds for all services
            const totalLength = Array.isArray(newServices)
              ? newServices.reduce((sum, s) => sum + ((s as any).length || 0) * (s.amount || 1), 0)
              : (selected.length || 0);
            // Compute new endTime if startTime is set
            let newEndTime = appointment.endTime;
            if (appointment.startTime && totalLength > 0) {
              newEndTime = addSecondsToTime(appointment.startTime, totalLength);
            }
            updateAppointment({ services: newServices as any, endTime: newEndTime });
            // clear validation error if this was the first service
            onClearError?.("services");
            setServiceSearch("");
            setServiceDialogOpen(false);
          };

          // Render button and dialog
          return (
            <>
              <Button
                variant="outline"
                className="border border-gray-300 rounded-md px-2 h-8 text-xs flex items-center"
                onClick={() => setServiceDialogOpen(true)}
                type="button"
              >
                <List className="w-3.5 h-3.5 mr-1" />
                {t("appointment.create.select_from_list", "Select from list")}
              </Button>
              <Dialog open={serviceDialogOpen} onOpenChange={setServiceDialogOpen}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{t("appointment.create.select_service", "Select service")}</DialogTitle>
                    <DialogDescription>
                      {t("appointment.create.select_service_description", "List of available services to add to the appointment. Select one.")}
                    </DialogDescription>
                  </DialogHeader>
                  {isLoadingServices && (
                    <div className="text-xs text-gray-500 py-4">{t("appointment.create.loading_services", "Loading services...")}</div>
                  )}
                  {servicesError && (
                    <div className="text-xs text-red-500 py-4">{t("appointment.create.services_error", "Error loading services")}</div>
                  )}
                  {!isLoadingServices && !servicesError && (
                    <div>
                      <Input
                        placeholder={t("appointment.create.search_service", "Search service...")}
                        value={serviceSearch}
                        onChange={(e) => setServiceSearch(e.target.value)}
                        className="mb-3"
                      />
                      <div className="max-h-64 overflow-y-auto">
                        {Array.isArray(availableServices) && availableServices.length > 0 ? (() => {
                          const term = serviceSearch.trim().toLowerCase();
                          const filtered = term.length >= 2
                            ? availableServices.filter((s) => s.title.toLowerCase().includes(term))
                            : availableServices;
                          if (filtered.length === 0) {
                            return <div className="text-xs text-gray-500 py-4">{t("common.nothing_found", "Nothing found")}</div>;
                          }
                          return (
                            <ul>
                              {filtered.map((service) => (
                                <li key={service.id}>
                                  <Button
                                    variant="ghost"
                                    className="w-full justify-between text-xs"
                                    onClick={() => handleAddService(service.id)}
                                  >
                                    <span>{service.title}</span>
                                    <span className="text-gray-500 ml-2">{formatDuration(service.length, t)}</span>
                                  </Button>
                                </li>
                              ))}
                            </ul>
                          );
                        })() : (
                          <div className="text-xs text-gray-500 py-4">{t("appointment.create.no_services", "No available services")}</div>
                        )}
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </>
          );
        })()}
        <div className="mb-4">
          {/* Services table */}
          <div className="mb-6">
            <div className="grid grid-cols-9 gap-2 mb-2 text-xs text-gray-500 px-1">
              <div className="col-span-6">{t("appointment.create.service_or_product", "Service or product")}</div>
              <div className="col-span-1 text-center">{t("appointment.create.quantity", "Qty")}</div>
              <div className="col-span-1 text-center">{t("appointment.create.price_byn", "Price")}</div>
              <div className="col-span-1"></div>
            </div>
            {Array.isArray(appointment.services) && appointment.services.map((service) => {
              // Handlers for quantity and removal
              const handleQuantityChange = (id: number | string, change: number) => {
                if (!Array.isArray(appointment.services)) return;
                const updatedServices = appointment.services.map((s) =>
                  s.id === id
                    ? { ...s, amount: Math.max(1, (s.amount || 1) + change) }
                    : s
                );
                updateAppointment({ services: updatedServices });
              };

              const handleRemoveService = (id: number | string) => {
                if (!Array.isArray(appointment.services)) return;
                const updatedServices = appointment.services.filter((s) => s.id !== id);
                updateAppointment({ services: updatedServices });
              };

              return (
                <div key={service.id} className="grid grid-cols-9 gap-2 mb-3 items-center">
                  <div className="col-span-6">
                    <div className="flex items-start">
                      <div className="mt-1 mr-2">
                        <div className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center">
                          <span className="text-[10px]">⚙️</span>
                        </div>
                      </div>
                      <div>
                        <div className="text-xs">{service.title}</div>
                        <div className="text-xs text-gray-500">{appointment.staff?.name || ""}</div>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-1 flex justify-center items-center">
                    <button
                      className="w-6 h-6 flex items-center justify-center text-gray-500"
                      onClick={() => handleQuantityChange(service.id, -1)}
                      type="button"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12" /></svg>
                    </button>
                    <span className="w-6 text-center text-xs">{typeof service.amount === "number" ? service.amount : 1}</span>
                    <button
                      className="w-6 h-6 flex items-center justify-center text-gray-500"
                      onClick={() => handleQuantityChange(service.id, 1)}
                      type="button"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                    </button>
                  </div>
                  <div className="col-span-1 text-center text-xs">{typeof service.cost_to_pay === "number" ? service.cost_to_pay : 0}</div>
                  <div className="col-span-1 flex justify-end">
                    <button
                      className="w-6 h-6 flex items-center justify-center text-gray-400"
                      onClick={() => handleRemoveService(service.id)}
                      type="button"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" /></svg>
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-xs mb-1">{t("appointment.create.date_time", "Date and time")}</label>
          <div className="grid grid-cols-3 gap-2">
            {/* Calendar popup for date selection */}
            {(() => {
              // --- Calendar popup state and logic ---
              const [calendarOpen, setCalendarOpen] = useState(false);
              const { data: schedule, isLoading: loadingSchedule, error: scheduleQueryError } = useStaffScheduleByDate();

              // Set error message if query fails
              const scheduleError = scheduleQueryError ? t("appointment.create.schedule_error", "Error loading schedule") : null;

              // Compute available dates for the selected master as a Set of YYYY-MM-DD
              const masterId = appointment?.staff?.id ? Number(appointment.staff.id) : undefined;
              const availableDates = React.useMemo(() => {
                if (!masterId || !schedule) return null;
                return new Set(
                  Object.entries(schedule)
                    .filter(([date, masterIds]) => Array.isArray(masterIds) && masterIds.includes(masterId))
                    .map(([date]) => date)
                );
              }, [masterId, schedule]);

              // Compute disabled days for May 2025 (or current month)
              const disabledDays = React.useMemo(() => {
                if (!masterId || !availableDates) return undefined;
                const start = DateTime.fromObject({ year: 2025, month: 5, day: 1 });
                const end = DateTime.fromObject({ year: 2025, month: 5, day: 31 });
                const days: Date[] = [];
                let d = start;
                while (d <= end) {
                  if (!availableDates.has(d.toISODate())) {
                    days.push(d.toJSDate());
                  }
                  d = d.plus({ days: 1 });
                }
                return days;
              }, [masterId, availableDates]);

              // Parse appointment.date to Date object
              const selectedDate = appointment.date ? DateTime.fromISO(appointment.date).toJSDate() : undefined;

              // Handle date select
              function handleDateSelect(date: Date | undefined) {
                if (!date) return;
                // Convert native Date to Luxon and set as ISO string (YYYY-MM-DD)
                const dt = DateTime.fromJSDate(date);
                const iso = dt.toISODate();
                updateAppointment({ date: iso });
                setCalendarOpen(false);
              }

              // Calendar classNames (match sidebar) — react-day-picker v9
              // keys (v8→v9 map in ui/calendar.tsx). Caption + nav hidden.
              // v9 puts modifier classes (selected/today/…) on the day CELL
              // (td), so the solid amber/orange look targets the inner
              // button via [&>button].
              const calendarClassNames = {
                months: "flex flex-col space-y-4",
                month: "space-y-4",
                month_caption: "hidden",
                caption_label: "text-sm font-medium capitalize",
                nav: "hidden",
                month_grid: "w-full border-collapse space-y-1",
                weekdays: "flex",
                weekday: "text-muted-foreground rounded-md w-7 font-normal text-[0.7rem]",
                week: "flex w-full mt-2",
                day: "h-7 w-7 text-center text-sm relative p-0 focus-within:relative focus-within:z-20",
                day_button: "h-7 w-7 p-0 font-normal hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
                selected:
                  "[&>button]:bg-amber-500 [&>button]:text-primary-foreground [&>button]:hover:bg-amber-500 [&>button]:hover:text-primary-foreground [&>button]:focus:bg-amber-500 [&>button]:focus:text-primary-foreground",
                today: "[&>button]:bg-orange-500 [&>button]:text-white [&>button]:rounded",
                outside: "text-muted-foreground opacity-50",
                disabled: "text-muted-foreground opacity-50",
                range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
                hidden: "invisible",
              };

              // --- Render ---
              return (
                <div className="relative">
                  <Input
                    type="text"
                    value={appointment.date ? formatDate(appointment.date) : ""}
                    className="pr-8 border-gray-300 rounded h-9 text-sm cursor-pointer"
                    readOnly
                    onClick={() => setCalendarOpen(true)}
                    tabIndex={0}
                  />
                  <span
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  >
                    <CalendarIcon className="w-3.5 h-3.5" />
                  </span>
                  {/* Calendar popup — opens UPWARD (bottom-full) so it isn't
                      clipped at the bottom of the modal on short viewports. */}
                  {calendarOpen && (
                    <div
                      className="absolute z-50 bg-white border rounded shadow-lg bottom-full mb-2 left-0"
                      style={{ minWidth: 240 }}
                      tabIndex={-1}
                      onBlur={e => {
                        // Close if focus leaves popup
                        if (!e.currentTarget.contains(e.relatedTarget as Node)) setCalendarOpen(false);
                      }}
                    >
                      <div className="p-2">
                        {loadingSchedule && (
                          <div className="text-xs text-gray-500 py-4">{t("appointment.create.loading_schedule", "Loading schedule...")}</div>
                        )}
                        {scheduleError && (
                          <div className="text-xs text-red-500 py-4">{scheduleError}</div>
                        )}
                        {!loadingSchedule && !scheduleError && (
                          <Calendar
                            mode="single"
                            selected={selectedDate}
                            onSelect={handleDateSelect}
                            locale={ru}
                            month={DateTime.fromObject({ year: 2025, month: 5, day: 1 }).toJSDate()}
                            defaultMonth={DateTime.fromObject({ year: 2025, month: 5, day: 1 }).toJSDate()}
                            showOutsideDays={true}
                            fixedWeeks
                            disabled={disabledDays}
                            className="w-full transform scale-[0.95] origin-top-left"
                            classNames={calendarClassNames}
                          />
                        )}
                      </div>
                      <div className="flex justify-end p-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setCalendarOpen(false)}
                          type="button"
                        >
                          {t("common.close", "Close")}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
            {/* StartTime Picker */}
            {(() => {
              const [startOpen, setStartOpen] = React.useState(false);
              const startRef = React.useRef<HTMLDivElement>(null);

              // Generate time options from 09:00 to 21:00 in 5-min steps
              const times: string[] = [];
              for (let h = 9; h <= 21; ++h) {
                for (let m = 0; m < 60; m += 5) {
                  if (h === 21 && m > 0) break;
                  times.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
                }
              }

              // Close on outside click
              React.useEffect(() => {
                function handle(e: MouseEvent) {
                  if (startRef.current && !startRef.current.contains(e.target as Node)) setStartOpen(false);
                }
                if (startOpen) document.addEventListener("mousedown", handle);
                return () => document.removeEventListener("mousedown", handle);
              }, [startOpen]);

              // Close on Escape
              React.useEffect(() => {
                function handle(e: KeyboardEvent) {
                  if (e.key === "Escape") setStartOpen(false);
                }
                if (startOpen) document.addEventListener("keydown", handle);
                return () => document.removeEventListener("keydown", handle);
              }, [startOpen]);

              return (
                <div className="relative" ref={startRef}>
                  <Input
                    type="text"
                    value={appointment.startTime || ""}
                    className="pr-8 border-gray-300 rounded h-9 text-sm cursor-pointer"
                    readOnly
                    onClick={() => setStartOpen((v) => !v)}
                    tabIndex={0}
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                    <CalendarIcon className="w-3.5 h-3.5" />
                  </span>
                  {startOpen && (
                    <div className="absolute z-50 bg-white border rounded shadow-lg mt-2 left-0 w-full max-h-60 overflow-y-auto">
                      {times.map((t) => (
                        <button
                          key={t}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-amber-100 ${appointment.startTime === t ? "bg-amber-200 font-bold" : ""
                            }`}
                          onClick={() => {
                            setStartOpen(false);
                            // When changing startTime, also update endTime based on current duration
                            const newEnd = addDurationToStart(t, duration.hours, duration.minutes);
                            updateAppointment({ startTime: t, endTime: newEnd });
                          }}
                          type="button"
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
            {/* EndTime Picker */}
            {(() => {
              const [endOpen, setEndOpen] = React.useState(false);
              const endRef = React.useRef<HTMLDivElement>(null);

              // Generate time options from 09:00 to 21:00 in 5-min steps
              const times: string[] = [];
              for (let h = 9; h <= 21; ++h) {
                for (let m = 0; m < 60; m += 5) {
                  if (h === 21 && m > 0) break;
                  times.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
                }
              }

              // Close on outside click
              React.useEffect(() => {
                function handle(e: MouseEvent) {
                  if (endRef.current && !endRef.current.contains(e.target as Node)) setEndOpen(false);
                }
                if (endOpen) document.addEventListener("mousedown", handle);
                return () => document.removeEventListener("mousedown", handle);
              }, [endOpen]);

              // Close on Escape
              React.useEffect(() => {
                function handle(e: KeyboardEvent) {
                  if (e.key === "Escape") setEndOpen(false);
                }
                if (endOpen) document.addEventListener("keydown", handle);
                return () => document.removeEventListener("keydown", handle);
              }, [endOpen]);

              return (
                <div className="relative" ref={endRef}>
                  <Input
                    type="text"
                    value={appointment.endTime || ""}
                    className="pr-8 border-gray-300 rounded h-9 text-sm cursor-pointer"
                    readOnly
                    onClick={() => setEndOpen((v) => !v)}
                    tabIndex={0}
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                    <CalendarIcon className="w-3.5 h-3.5" />
                  </span>
                  {endOpen && (
                    <div className="absolute z-50 bg-white border rounded shadow-lg mt-2 left-0 w-full max-h-60 overflow-y-auto">
                      {times.map((t) => (
                        <button
                          key={t}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-amber-100 ${appointment.endTime === t ? "bg-amber-200 font-bold" : ""
                            }`}
                          onClick={() => {
                            setEndOpen(false);
                            updateAppointment({ endTime: t });
                          }}
                          type="button"
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
          {/* the master's own calendar or Booksy holds this time: a warning, never a refusal */}
          <BusyOverlapWarning
            staffId={Number(appointment.specialistId ?? appointment.staff?.id)}
            date={appointment.date}
            startTime={appointment.startTime}
            endTime={appointment.endTime}
          />
        </div>

        {/* Duration section */}
        {(() => {
          // Handler for hours change
          function handleHoursChange(e: React.ChangeEvent<HTMLSelectElement>) {
            const newHours = Number(e.target.value);
            setDuration((prev) => ({ ...prev, hours: newHours }));
            if (appointment!.startTime) {
              const newEndTime = addDurationToStart(appointment!.startTime, newHours, duration.minutes);
              updateAppointment({ endTime: newEndTime });
            }
          }

          // Handler for minutes change
          function handleMinutesChange(e: React.ChangeEvent<HTMLSelectElement>) {
            const newMinutes = Number(e.target.value);
            setDuration((prev) => ({ ...prev, minutes: newMinutes }));
            if (appointment!.startTime) {
              const newEndTime = addDurationToStart(appointment!.startTime, duration.hours, newMinutes);
              updateAppointment({ endTime: newEndTime });
            }
          }

          const disabled = !appointment.startTime;

          return (
            <div className="mb-4">
              <label className="block text-xs mb-1">{t("appointment.create.duration", "Duration")}</label>
              <div className="grid grid-cols-2 gap-2">
                <div className="relative">
                  <select
                    className="w-full h-9 px-3 border border-gray-300 rounded-md appearance-none text-sm"
                    value={duration.hours}
                    onChange={handleHoursChange}
                    disabled={disabled}
                  >
                    {Array.from({ length: 13 }, (_, i) => (
                      <option key={i} value={i}>
                        {i} {t("common.time.hour", "hour")}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <svg className="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
                <div className="relative">
                  <select
                    className="w-full h-9 px-3 border border-gray-300 rounded-md appearance-none text-sm"
                    value={duration.minutes}
                    onChange={handleMinutesChange}
                    disabled={disabled}
                  >
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i * 5} value={i * 5}>
                        {i * 5} {t("common.time.minutes", "minutes")}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <svg className="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
              {disabled && (
                <div className="text-xs text-gray-400 mt-1">{t("appointment.create.select_start_time_first", "Select start time first")}</div>
              )}
            </div>
          );
        })()}

        <div className="mb-4">
          <label className="block text-xs mb-1">{t("appointment.create.comment", "Comment")}</label>
          <textarea
            className="w-full border border-gray-300 rounded-md p-2 min-h-[100px] text-sm"
            placeholder={t("appointment.create.comment_placeholder", "Add a comment to the appointment")}
            value={comment}
            onChange={e => {
              setComment(e.target.value);
              handleCommentChange(e.target.value);
            }}
          ></textarea>
        </div>
      </div>

    </div>
  )
}
